import React, { FC, forwardRef, HTMLAttributes, ReactNode, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { TIcons } from '../../type/icons-type';
import Item from './Item';
import List from './List';
import { useGetPendingApprovalCounts } from '../../apis/hooks/pendingApprovalsCount';

interface INavigationLineProps {
	className?: string;
}
export const NavigationLine: FC<INavigationLineProps> = ({ className }) => {
	return <hr className={classNames('navigation-line', className)} />;
};

interface INavigationTitleProps extends HTMLAttributes<HTMLSpanElement> {
	className?: string;
	children: ReactNode;
}
export const NavigationTitle: FC<INavigationTitleProps> = ({ className, children, ...props }) => {
	return (
		<li className='navigation-item'>
			<span className={classNames('navigation-title', className)} {...props}>
				{children}
			</span>
		</li>
	);
};

interface INavigationProps {
	horizontal?: boolean;
	menu: {
		[key: string]: {
			id?: string | number;
			text?: string;
			path?: string;
			icon?: TIcons;
			isDisable?: boolean;
			subMenu?: {
				[key: string]: {
					id?: string | number;
					text?: string;
					path?: string;
					icon?: TIcons;
					isDisable?: boolean;
				};
			} | null;
		};
	};
	id: string;
	className?: string;
}
const Navigation = forwardRef<HTMLElement, INavigationProps>(
	({ menu, horizontal, id, className, ...props }, ref) => {
		const [activeItem, setActiveItem] = useState(undefined);

		const { t } = useTranslation('menu');


		const { data: counts } = useGetPendingApprovalCounts();

		const enhancedMenu = useMemo(() => {
			if (!counts) return menu;

			// 1. Bridge the API response keys to your Menu Object Keys
			const countMapping: Record<string, number> = {
				requisitionPage: counts?.requisition,
				purchaseIndentPage: counts?.purchaseIndent,
				purchaseOrderPage: counts?.purchaseOrder,
				purchasePages: counts?.purchaseBill,
				modifyPurchaseOrderPage: counts?.modifyPORequest,
				attendanceRequest: counts?.attendanceRequest,
				paymentVerificationPage: counts?.paymentVerification,
				// Add chequeSignature if you have a specific page for it
			};

			// Deep clone to avoid mutating the original prop
			const newMenu = JSON.parse(JSON.stringify(menu));

			Object.keys(newMenu).forEach((key) => {
				const item = newMenu[key];

				// 2. Apply count to Top Level items
				if (countMapping[key] > 0) {
					item.notification = countMapping[key];
				}

				// 3. Apply counts to Sub-Menus (e.g., inside Verification Process)
				if (item.subMenu) {
					Object.keys(item.subMenu).forEach((subKey) => {
						// Check if the sub-menu key (like 'requisition' inside verification) has a count
						// We check both the subKey and the mapping
						const subCount =
							countMapping[subKey] || counts[subKey as keyof typeof counts];
						if (subCount > 0) {
							item.subMenu[subKey].notification = subCount;
						}
					});
				}
			});

			return newMenu;
		}, [menu, counts]);

		function fillMenu(
			data:
				| {
					id?: string | number;
					text?: string;
					path?: string;
					icon?: TIcons;
					isDisable?: boolean;
					subMenu?:
					| {
						id?: string | number;
						text?: string;
						path?: string;
						icon?: TIcons;
						isDisable?: boolean;
					}[]
					| undefined;
				}[]
				| any,
			parentId: string,
			rootId: string,
			isHorizontal: boolean | undefined,
			isMore: boolean | undefined,
		) {
			return Object.keys(data).map((item) =>
				data[item].path ? (
					<Item
						key={data[item].id}
						rootId={rootId}
						id={data[item].id}
						title={data[item].text}
						icon={data[item].icon}
						to={`${data[item].path}`}
						parentId={parentId}
						isHorizontal={isHorizontal}
						setActiveItem={setActiveItem}
						activeItem={activeItem}
						notification={data[item].notification}
						hide={data[item].hide}>
						{!!data[item].subMenu &&
							fillMenu(
								data[item].subMenu,
								data[item].id,
								rootId,
								isHorizontal,
								undefined,
							)}
					</Item>
				) : (
					!isMore &&
					!isHorizontal && (
						<NavigationTitle key={data[item].id}>
							{t(data[item].text) as ReactNode}
						</NavigationTitle>
					)
				),
			);
		}

		return (
			// @ts-ignore

			<nav ref={ref} aria-label={id} className={className} {...props}>
				<List id={id} horizontal={horizontal}>
					{fillMenu(enhancedMenu, id, id, horizontal, undefined)}
					{horizontal && (
						<Item
							rootId={`other-${id}`}
							title={t('More') as string}
							icon='MoreHoriz'
							isHorizontal
							isMore>
							{fillMenu(enhancedMenu, `other-${id}`, `other-${id}`, false, true)}
						</Item>
					)}
				</List>
			</nav>
		);
	},
);
Navigation.displayName = 'Navigation';

export default Navigation;
